import { Button, Flex, Heading, RadioCards, Text } from '@hubertai/hubert-component-library';
import { Table } from '@radix-ui/themes';
import { Check, Eye, ShieldCheck, UsersRound } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { KeyedRole, RoleKey } from './roles';
import { assignableRoles, ROLE_KEYS, roleRank } from './roles';

const icons: Record<RoleKey, ReactNode> = {
  viewer: <Eye size={18} aria-hidden="true" />,
  'group-admin': <UsersRound size={18} aria-hidden="true" />,
  'super-admin': <ShieldCheck size={18} aria-hidden="true" />,
};

const selectedCardStyle = { '--radio-cards-item-background-color': 'var(--accent-a2)' } as CSSProperties;

const comparisonRows: { row: string; from: RoleKey }[] = [
  { row: 'seeGroup', from: 'viewer' },
  { row: 'seeShared', from: 'viewer' },
  { row: 'editGroup', from: 'group-admin' },
  { row: 'manageGroup', from: 'group-admin' },
  { row: 'shareOrg', from: 'super-admin' },
  { row: 'manageAll', from: 'super-admin' },
];

type RoleCardsProps = {
  roles: KeyedRole[];
  value: string;
  onValueChange: (roleId: string) => void;
  canAssignSuperAdmin: boolean;
};

const RoleCards = ({ roles, value, onValueChange, canAssignSuperAdmin }: RoleCardsProps) => {
  const { t } = useTranslation();
  const [showComparison, setShowComparison] = useState(false);
  const roleText = (key: RoleKey, part: string) => t(`settings.accessGroupRoles.${key}.${part}`);

  return (
    <div className="flex flex-col gap-2">
      <RadioCards.Root
        value={value}
        onValueChange={onValueChange}
        columns="1"
        aria-label={t('settings.accessGroupRoles.ariaLabel')}
      >
        {assignableRoles(roles, canAssignSuperAdmin).map((role) => {
          const key = role.key as RoleKey;
          return (
            <RadioCards.Item key={role.id} value={role.id} style={value === role.id ? selectedCardStyle : undefined}>
              <Flex direction="column" gap="1" width="100%">
                <Flex align="center" gap="2">
                  {icons[key]}
                  <Heading size="3" asChild>
                    <span>{roleText(key, 'name')}</span>
                  </Heading>
                </Flex>
                <Text size="2" tone="subtle">
                  {roleText(key, 'summary')}
                </Text>
              </Flex>
            </RadioCards.Item>
          );
        })}
      </RadioCards.Root>

      <Button
        type="button"
        variant="ghost"
        color="gray"
        className="self-start"
        onClick={() => setShowComparison((shown) => !shown)}
      >
        {showComparison ? t('settings.accessGroupRoles.hideCompare') : t('settings.accessGroupRoles.compare')}
      </Button>
      {showComparison && (
        <Table.Root size="1">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell />
              {ROLE_KEYS.map((key) => (
                <Table.ColumnHeaderCell key={key} justify="center">
                  {roleText(key, 'name')}
                </Table.ColumnHeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {comparisonRows.map(({ row, from }) => (
              <Table.Row key={row}>
                <Table.RowHeaderCell>{t(`settings.accessGroupRoles.comparison.${row}`)}</Table.RowHeaderCell>
                {ROLE_KEYS.map((key) => (
                  <Table.Cell key={key} justify="center">
                    {roleRank(key) >= roleRank(from) && (
                      <Check size={16} className="mx-auto" aria-label={t('settings.accessGroupRoles.included')} />
                    )}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </div>
  );
};

export default RoleCards;
